export type BackoffOptions = {
  /** First retry delay; each further attempt doubles from here. */
  baseMs?: number
  /** Ceiling — a long outage must not push the next try hours out. */
  maxMs?: number
  /** Never schedule closer than this, even when jitter draws ~0. */
  floorMs?: number
  /** Injectable for tests; defaults to Math.random. */
  random?: () => number
}

/**
 * Exponential backoff with **full jitter**.
 *
 * The jitter is not decoration: a terminal runs many tablets against one access
 * point, and when wifi returns they would otherwise all retry on the same
 * millisecond and hammer the API together. Spreading each device randomly
 * across its window turns that spike into a trickle.
 */
export function nextDelayMs(attempt: number, options: BackoffOptions = {}): number {
  const baseMs = options.baseMs ?? 1_000
  const maxMs = options.maxMs ?? 30_000
  const floorMs = options.floorMs ?? 250
  const random = options.random ?? Math.random

  const safeAttempt = Math.max(1, Math.floor(attempt) || 1)
  // Cap the exponent before the multiply so a huge attempt count can't overflow.
  const exponent = Math.min(safeAttempt - 1, 30)
  const window = Math.min(baseMs * 2 ** exponent, maxMs)

  return Math.max(floorMs, Math.round(window * random()))
}
